import { PromptAddendumModel } from "./getLandlordResearchPrompt";
import { PromptRuleFields } from "./landlordResearchPromptOps";
import { PeoplePromptRevision } from "./getPeopleRankingPrompt";
import { PeopleRole , taskForRole } from "../models/peopleCandidate";
import axiosConfig from "./axiosConfig";

// Every people-prompt write returns the fresh active revision + recent history, so
// the card re-renders from one shape whichever route it called. Mirrors
// LandlordPromptWriteResponse; the people BE additionally returns `revisionsTruncated`.
export interface PeoplePromptWriteResponse {
  active: PeoplePromptRevision;
  revisions: PeoplePromptRevision[];
  revisionsTruncated: boolean;
}

const auth = (token: string) => ({
  headers: { Authorization: `Bearer ${token}` },
});

// Add (no ruleId) or update one rule.
export const savePeoplePromptRule = async (
  authToken: string,
  body: {
    role: PeopleRole;
    baseRevision: number;
    ruleId?: string | null;
    rule: PromptRuleFields;
    status?: string;
    note?: string;
  }
): Promise<PeoplePromptWriteResponse> => {
  const { data } = await axiosConfig.post(
    `/leads/ai-prompt/rule`,
    { ...body, task: taskForRole(body.role) },
    auth(authToken)
  );
  return data;
};

// Delete one rule outright (recoverable by restoring a revision that had it).
export const deletePeoplePromptRule = async (
  authToken: string,
  body: {
    role: PeopleRole;
    baseRevision: number;
    ruleId: string;
    note?: string;
  }
): Promise<PeoplePromptWriteResponse> => {
  const { data } = await axiosConfig.post(
    `/leads/ai-prompt/rule/delete`,
    { ...body, task: taskForRole(body.role) },
    auth(authToken)
  );
  return data;
};

// Re-position rules to match `ruleIds` (rules omitted keep their order after).
export const reorderPeoplePromptRules = async (
  authToken: string,
  body: {
    role: PeopleRole;
    baseRevision: number;
    ruleIds: string[];
    note?: string;
  }
): Promise<PeoplePromptWriteResponse> => {
  const { data } = await axiosConfig.post(
    `/leads/ai-prompt/reorder`,
    { ...body, task: taskForRole(body.role) },
    auth(authToken)
  );
  return data;
};

// Edit the free-text preamble half of the prompt.
export const savePeoplePromptPreamble = async (
  authToken: string,
  body: {
    role: PeopleRole;
    baseRevision: number;
    preamble: string;
    note?: string;
  }
): Promise<PeoplePromptWriteResponse> => {
  const { data } = await axiosConfig.post(
    `/leads/ai-prompt/preamble`,
    { ...body, task: taskForRole(body.role) },
    auth(authToken)
  );
  return data;
};

// Whole-set save: replace the entire addendum in one revision (bulk path).
export const savePeoplePromptAddendum = async (
  authToken: string,
  body: {
    role: PeopleRole;
    baseRevision: number;
    addendum: PromptAddendumModel;
    note?: string;
  }
): Promise<PeoplePromptWriteResponse> => {
  const { data } = await axiosConfig.post(
    `/leads/ai-prompt`,
    { ...body, task: taskForRole(body.role) },
    auth(authToken)
  );
  return data;
};

// Restore an earlier revision by appending a copy of it.
export const restorePeoplePromptRevision = async (
  authToken: string,
  body: {
    role: PeopleRole;
    baseRevision: number;
    revisionNumber: number;
    note?: string;
  }
): Promise<PeoplePromptWriteResponse> => {
  const { data } = await axiosConfig.post(
    `/leads/ai-prompt/restore`,
    { ...body, task: taskForRole(body.role) },
    auth(authToken)
  );
  return data;
};
